/**
 * MagicLinkVerify — completes a magic-link login.
 *
 * Route: /auth/magic-link/verify?token=…
 * Calls POST /api/auth/magic-link/verify, stores the JWT via AuthContext,
 * and redirects to the path captured at request time (defaults /customer).
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { applyAuthRedirect } from "../lib/subdomains";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

export default function MagicLinkVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("verifying");   // verifying | success | error
  const [message, setMessage] = useState("Signing you in…");

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setStatus("error"); setMessage("No token provided"); return; }
    (async () => {
      try {
        const r = await axios.post(`${API_BASE}/auth/magic-link/verify`, { token });
        localStorage.setItem("ashra_token", r.data.token);
        await refresh?.();
        setStatus("success");
        setMessage("You're in — redirecting…");
        // Cross-subdomain hand-off when redirect_url points elsewhere.
        setTimeout(() => {
          if (r.data.redirect_url) {
            applyAuthRedirect(r.data, r.data.redirect_path || "/customer");
          } else {
            navigate(r.data.redirect_path || "/customer", { replace: true });
          }
        }, 500);
      } catch (e) {
        setStatus("error");
        setMessage(e?.response?.data?.detail || "This link is invalid or expired. Request a new one.");
      }
    })();
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAFA] p-8" data-testid="magic-link-verify">
      <div className="surface p-8 w-[min(420px,92vw)] text-center">
        {status === "verifying" && (<><Loader2 className="h-8 w-8 text-ai mx-auto animate-spin" /><p className="mt-3 font-display text-lg font-bold text-ink">{message}</p></>)}
        {status === "success" && (<><CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" /><p className="mt-3 font-display text-lg font-bold text-ink">{message}</p></>)}
        {status === "error" && (
          <>
            <AlertCircle className="h-8 w-8 text-rose-500 mx-auto" />
            <p className="mt-3 font-display text-lg font-bold text-ink">Sign-in link issue</p>
            <p className="text-sm text-meta mt-1">{message}</p>
            <button onClick={() => navigate("/login", { replace: true })} className="mt-4 rounded-full ashra-gradient-bg px-4 py-2 text-xs font-semibold text-white">Back to sign-in</button>
          </>
        )}
      </div>
    </div>
  );
}
